<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { AddTreeGraphNode, CreateStep, GraphEntityType, Mutation } from '@/generated/graphql'
import { reactive, ref } from 'vue'
import ToolPicker from '@/components/input/ToolPicker.vue'
import StepTypePicker from '@/components/input/StepTypePicker.vue'
import TextAreaField from '@/components/input/TextAreaField.vue'
import TextField from '@/components/input/TextField.vue'
import useVuelidate from '@vuelidate/core'
import { scrollToFirstInvalidField } from '@/validation'
import { useMutation } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import NotificationSaved from '@/components/notifications/NotificationSaved.vue'
import { v4 } from 'uuid'
import DurationField from '@/components/input/DurationField.vue'
import { createStepValidationRules } from '@/validation/step'

type Model = Partial<CreateStep>

const props = defineProps<{
  productId: string
  moduleId: string
  siteId: string
  parentNodeId?: string
  initName?: string
  initInstructionText?: string
  initDurationInSeconds?: number
  withoutTitle?: boolean
}>()
const emit = defineEmits<{
  created: [id: string]
}>()

const { t } = useI18n()

const model = reactive<Model>({
  id: v4(),
  productId: props.productId,
  toolIds: [],
  name: props.initName,
  instructionText: props.initInstructionText,
  durationInSeconds: props.initDurationInSeconds || 0,
})
const validation = useVuelidate<Model>(createStepValidationRules, model)
const notifySaved = ref(false)

const createMutation = useMutation<Mutation, { step: CreateStep; node: AddTreeGraphNode }>(gql`
  mutation createStepAndAddNodeToModuleGraph($step: CreateStep!, $node: AddTreeGraphNode!) {
    product {
      createStep(command: $step) {
        id
      }
      addNodeToModuleGraph(command: $node)
    }
  }
`)
function create() {
  validation.value.$touch()
  if (validation.value.$invalid) {
    scrollToFirstInvalidField()
    return
  }

  const stepId = model.id as string
  const node: AddTreeGraphNode = {
    entityId: props.moduleId,
    node: {
      entityType: GraphEntityType.Step,
      entityId: stepId,
      parentId: props.parentNodeId,
      id: v4(),
    },
  }

  createMutation
    .mutate({
      step: model as CreateStep,
      node,
    })
    .then(() => {
      notifySaved.value = true
      emit('created', stepId)
    })
}
</script>

<template>
  <slot v-if="!props.withoutTitle" name="title">
    <h2>{{ t('component.createStep.title') }}</h2>
  </slot>

  <v-card>
    <v-card-text>
      <text-field
        v-model="model.name"
        :label="t('entity.step.field.name')"
        :validation="validation.name"
        required
      />
      <step-type-picker
        v-model="model.stepTypeId"
        :label="t('entity.step.field.stepType')"
        :validation="validation.stepTypeId"
        required
      />
      <duration-field
        v-model="model.durationInSeconds"
        :label="t('entity.step.field.durationInSeconds')"
        :validation="validation.durationInSeconds"
      />
      <text-area-field
        v-model="model.instructionText"
        :label="t('entity.step.field.instructionText')"
        :validation="validation.instructionText"
        markdown
      />
      <tool-picker
        v-model="model.toolIds"
        :site-id="props.siteId"
        :label="t('entity.step.field.tools')"
      />
    </v-card-text>
    <v-card-actions>
      <v-spacer />
      <v-btn
        color="primary"
        variant="elevated"
        :loading="createMutation.loading.value"
        @click="create()"
      >
        {{ t('button.save') }}
      </v-btn>
    </v-card-actions>
  </v-card>

  <notification-saved v-model="notifySaved" />
</template>

<style scoped lang="scss"></style>
